import { forwardRef, KeyboardEvent, useEffect, useRef, useState } from 'react';

import { ChevronDown } from '@design-system/icons';
import { TextInput } from '@design-system/ui';
import { cn } from '@foundation/utils/cn';

type AutocompleteSelectOption = {
  readonly label: string;
  readonly value: string;
};

type AutocompleteSelectInputProps = {
  readonly error?: string;
  readonly label?: string;
  readonly name?: string;
  readonly noResultsText: string;
  readonly onBlur?: () => void;
  readonly onChange: (value: string) => void;
  readonly options: ReadonlyArray<AutocompleteSelectOption>;
  readonly required?: boolean;
  readonly value?: string;
};

export const AutocompleteSelectInput = forwardRef<
  HTMLInputElement,
  AutocompleteSelectInputProps
>(function AutocompleteSelectInput(
  {
    error,
    label,
    name,
    noResultsText,
    onBlur,
    onChange,
    options,
    required,
    value,
  },
  ref,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const close = () => {
    setIsOpen(false);
    setQuery('');
  };

  const selectOption = (option: AutocompleteSelectOption) => {
    onChange(option.value);
    close();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      close();
      return;
    }

    if (
      filteredOptions.length > 0 &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    ) {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((currentIndex) => {
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        return (
          (currentIndex + direction + filteredOptions.length) %
          filteredOptions.length
        );
      });
      return;
    }

    if (event.key === 'Enter' && isOpen && filteredOptions[activeIndex]) {
      event.preventDefault();
      selectOption(filteredOptions[activeIndex]);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
    >
      <TextInput
        ref={ref}
        name={name}
        required={required}
        label={label}
        error={error}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={`${name}-options`}
        autoComplete="off"
        value={isOpen ? query : selectedOption?.label || ''}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          setQuery('');
          setIsOpen(true);
        }}
        onBlur={(event) => {
          if (!containerRef.current?.contains(event.relatedTarget)) {
            close();
            onBlur?.();
          }
        }}
        onKeyDown={handleKeyDown}
        endAdornment={
          <ChevronDown
            size={16}
            className={cn('transition-transform', {
              'rotate-180': isOpen,
            })}
          />
        }
      />

      {isOpen && (
        <ul
          id={`${name}-options`}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-primary bg-white shadow-lg"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
              >
                <button
                  type="button"
                  onClick={() => selectOption(option)}
                  className={cn(
                    'w-full px-4 py-3 text-left text-base transition-colors hover:bg-secondary focus:bg-secondary focus:outline-none',
                    { 'bg-secondary': index === activeIndex },
                  )}
                >
                  {option.label}
                </button>
              </li>
            ))
          ) : (
            <li className="px-4 py-3 text-center text-sm text-tertiary">
              {noResultsText}
            </li>
          )}
        </ul>
      )}
    </div>
  );
});

AutocompleteSelectInput.displayName = 'AutocompleteSelectInput';
